// =============================================================================
// ui/state/useAuthUser.ts — who is signed in, from the auth service itself.
// -----------------------------------------------------------------------------
// ── ⚠ NOT `useSupabase().userId`, AND THE DIFFERENCE IS THE POINT ──
//
// The context carries the id because every read is scoped by it. This asks the
// auth service for the whole user — email, created-at, id — which is what the
// profile card renders, and it asks FRESH rather than trusting a value captured
// at boot.
//
// That matters after an email change: `updateUser({ email })` does not take
// effect until the confirmation link is clicked, and the session in memory can
// be minutes or days stale by the time someone opens this screen to check
// whether the change landed. Re-reading is the only way the card can answer the
// question the user came to ask.
//
// The old client does the same thing for the same reason — its account tab opens
// with `sb.auth.getUser()` rather than reading its own state.
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSupabase } from './supabase';
import { reportFailureAs } from '../../io/diagnostics/errorLog';

/**
 * The readable text of whatever a failure turns out to be.
 *
 * ── ⚠ IT READS `.message` OFF ANY SHAPE, AND THE ORIGINAL REASON WAS WRONG ──
 *
 * This block used to say: "Supabase's auth errors are plain objects with a
 * `message`, NOT `Error` instances", and that claim spread to four more files.
 *
 * 🔴 Checked against the INSTALLED library on 2026-08-27: `AuthError extends
 * Error` (auth-js/dist/main/lib/errors.js:22), and so does `PostgrestError`.
 * `e instanceof Error ? e.message : String(e)` would have been correct all
 * along.
 *
 * ▶ The belief came from THIS REPO'S OWN TEST DOUBLES, which resolve bare
 * `{ message: '…' }` objects — so the test that "caught" it was asserting
 * against a mock the library does not produce. A comment derived from a
 * fixture, believed for weeks, and copied outward.
 *
 * ⚠ THE FUNCTION STAYS. It is correct under both shapes, and the bare-object
 * shape is what the suite actually exercises. What changes is the reason. See #52.
 *
 * ⚠ Same family as the beta PNG bug (D107): a plausible-looking fallback on an
 * error path nobody exercises is an assertion nobody checked.
 */
function readableError(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (e && typeof e === 'object' && typeof (e as { message?: unknown }).message === 'string') {
    return (e as { message: string }).message;
  }
  if (typeof e === 'string') return e;
  // ⚠ Says it is unknown rather than inventing a cause. The user is about to
  // quote this to support.
  return 'The account service gave no reason.';
}

export type AuthUserPhase = 'loading' | 'ready' | 'failed';

export interface AuthUser {
  id: string;
  email: string;
  createdAt: string | null;
}

export interface AuthUserState {
  phase: AuthUserPhase;
  /** ⚠ NULL until a read succeeded — never a blank user with empty fields. */
  user: AuthUser | null;
  error: string | null;
  reload: () => void;
}

export function useAuthUser(): AuthUserState {
  const { sb } = useSupabase();
  const [phase, setPhase] = useState<AuthUserPhase>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    setError(null);

    void (async () => {
      try {
        const { data, error: e } = await sb.auth.getUser();
        if (cancelled) return;
        if (e) throw e;
        const u = data?.user;
        // ⚠ "No user" is an ERROR state here, not an empty profile. This screen
        // only renders inside an authenticated shell, so a null user means the
        // session died underneath it — and a profile card showing blanks would
        // read as "your account has no email".
        if (!u) throw new Error('Not signed in.');
        setUser({
          id: u.id,
          // ⚠ Beta's fallback, kept: an account can genuinely have no email
          // (a future OAuth path), and an empty <b></b> looks like a bug.
          email: u.email || '(no email)',
          createdAt: u.created_at ?? null,
        });
        setPhase('ready');
      } catch (e) {
        if (cancelled) return;
        // ⚠ `reportFailureAs` — `readableError` reads `.message` off either shape;
        // see the note on that function for why its original reason was wrong.
        setError(reportFailureAs('sign-in', e, readableError(e)));
        setPhase('failed');
      }
    })();

    return () => { cancelled = true; };
  }, [sb, nonce]);

  return useMemo(
    () => ({ phase, user, error, reload }),
    [phase, user, error, reload],
  );
}
